import { jsPDF } from 'jspdf';

// Salary slip PDF — matches the approved sample (sample-salary-slip.pdf):
// BCPL logo centred, company name + address, blue rule, SALARY SLIP + month,
// employee details block, two-column EARNINGS / DEDUCTIONS table, footer notes.
//
// Details block (02-10-2026): Name / Code, Designation / Department, UAN /
// ESIC Number, Payment Mode / Pay Period, Account Number / IFSC. A field with
// nothing on the employee record prints "NA" — never a blank or a dash, which
// reads like a printing fault. "ESIC Number" is the employee's insurance
// number (employees.esic_number); the ESIC rupee deduction is the separate
// "ESIC / Insurance" line in the table (payslips.esic).
//
// Attendance-based slips (Sept 2026 onward) additionally show, from STORED
// figures only (nothing is recomputed here):
//   * the leave deduction as its own DEDUCTIONS line ("Leave Without Pay
//     (N days)") — gross is never silently reduced, the named line keeps the
//     arithmetic honest and answerable. This line is where the unpaid days
//     are stated; the details block no longer repeats Days Paid / Days
//     Unpaid (Days Not Employed still appears there, only when non-zero),
//   * a NET PAY band under the table (decision 05-09-2026: the original
//     no-net-pay rule predates a month-to-month leave deduction; now that
//     take-home varies, the slip states it rather than making the employee
//     do the arithmetic).
// Pre-attendance slips (July 2026) have all of these fields NULL — no leave
// line, no net pay.
//
// renderPayslip() is pure drawing (no DOM, no Supabase) so it runs in Node for
// specimen generation as well as in the browser.

// ─── Colors (from the approved sample) ────────────────────────────────────────
const INK    = [31, 56, 100];    // dark navy-blue headings / values
const BLUE   = [46, 116, 181];   // horizontal rules
const BAND   = [26, 43, 74];     // table header band
const GREY   = [120, 128, 138];  // labels, address, footer notes
const HAIR   = [214, 219, 226];  // hairline row separators

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const monthLabel = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

// Full account numbers must not travel over WhatsApp: mask to last four.
export const maskAccount = (acct) => {
  const a = String(acct || '').trim();
  if (!a) return '—';
  return 'XXXXXX' + a.slice(-4);
};

// Details-block value: anything empty prints "NA" (the UAN convention).
const orNA = (v) => String(v ?? '').trim() || 'NA';

const inr = (v) => (v === null || v === undefined
  ? '—'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Browser-side logo loader (Node specimen scripts base64 the file directly).
// Downscaled before embedding: the source PNG is 1080x1080 and would bloat
// every slip to ~4.5 MB; at 26mm wide on the page, 360px is ample.
export async function loadBcplLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const max = 360;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      // The slip page is white, so flatten transparency onto white and emit
      // JPEG — keeps each of the ~52 monthly slips small for WhatsApp.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.88), aspect: img.width / img.height });
    };
    img.onerror = () => resolve(null);
    img.src = '/BCPL_Logo.png';
  });
}

// d: { logo: {dataUrl, aspect}|null, monthText, name, employeeCode,
//      designation, department, uan, esicNumber, paymentMode, accountMasked,
//      ifsc, basic, da, residualPay, gross, pf, esic, professionalTax,
//      totalDeductions, daysUnpaid?, daysNotEmployed?, lwpDeduction?, netPay? }
// The optional fields are null on pre-attendance slips and omit their rows.
const has = (v) => v !== null && v !== undefined;

// Draw a details value inside its cell: shrink the type until it fits, and
// only then cut it — a long designation must not run into the next column.
function drawValue(doc, text, x, y, maxW) {
  let size = 9.2;
  doc.setFontSize(size);
  while (doc.getTextWidth(text) > maxW && size > 7) {
    size -= 0.3;
    doc.setFontSize(size);
  }
  let out = text;
  while (out.length > 1 && doc.getTextWidth(out === text ? out : `${out}...`) > maxW) out = out.slice(0, -1);
  doc.text(out === text ? out : `${out.trimEnd()}...`, x, y);
}

export function renderPayslip(doc, d) {
  const W = 210, M = 16, CW = W - 2 * M;
  const center = W / 2;
  let y = 10;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 297, 'F');

  // ── Logo, centred ───────────────────────────────────────────────────────────
  if (d.logo?.dataUrl) {
    const lw = 26, lh = lw / (d.logo.aspect || 1);
    const fmt = d.logo.dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
    doc.addImage(d.logo.dataUrl, fmt, center - lw / 2, y, lw, lh);
    y += lh + 6;
  } else {
    y += 8;
  }

  // ── Company name + address ──────────────────────────────────────────────────
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13.5);
  doc.text('BRAHMAPUTRA CRUISE PRIVATE LIMITED', center, y, { align: 'center' });
  y += 5.5;
  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.4);
  doc.text('289, A T Road, Maa Padma Commercial Complex, Guwahati, Assam - 781009', center, y, { align: 'center' });
  y += 4.4;
  doc.text('Email: info@bcpl.in', center, y, { align: 'center' });
  y += 5;

  // ── Blue rule ───────────────────────────────────────────────────────────────
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.7);
  doc.line(M, y, W - M, y);
  y += 10;

  // ── Title ───────────────────────────────────────────────────────────────────
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.text('SALARY SLIP', center, y, { align: 'center' });
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(`For the month of ${d.monthText}`, center, y, { align: 'center' });
  y += 12;

  // ── Employee details block ──────────────────────────────────────────────────
  const rows = [
    [['Employee Name', orNA(d.name)], ['Employee Code', orNA(d.employeeCode)]],
    [['Designation', orNA(d.designation)], ['Department', orNA(d.department)]],
    [['UAN', orNA(d.uan)], ['ESIC Number', orNA(d.esicNumber)]],
    [['Payment Mode', orNA(d.paymentMode)], ['Pay Period', d.monthText]],
    [['Account Number', orNA(d.accountMasked)], ['IFSC', orNA(d.ifsc)]],
    // Only when non-zero (today a generated slip always has 0 — dashes block).
    ...(d.daysNotEmployed > 0 ? [[['Days Not Employed', String(d.daysNotEmployed)], null]] : []),
  ];
  const rowH = 9.5;
  const leftLabelX = M + 2, leftValueX = M + 42;
  const rightLabelX = center + 6, rightValueX = center + 48;
  const leftValueW = rightLabelX - leftValueX - 3, rightValueW = W - M - rightValueX - 1;

  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.2);
  doc.line(M, y - 5.5, W - M, y - 5.5);
  for (const [left, right] of rows) {
    doc.setFontSize(8.4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GREY);
    doc.text(left[0], leftLabelX, y);
    if (right) doc.text(right[0], rightLabelX, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    drawValue(doc, String(left[1]), leftValueX, y, leftValueW);
    if (right) drawValue(doc, String(right[1]), rightValueX, y, rightValueW);
    doc.setDrawColor(...HAIR);
    doc.line(M, y + 3.5, W - M, y + 3.5);
    y += rowH;
  }
  y += 8;

  // ── Earnings / deductions table ─────────────────────────────────────────────
  const half = CW / 2;
  const tX = M, tY = y;
  const headH = 9, cellH = 9;
  const leftAmtX = tX + half - 4, rightAmtX = tX + CW - 4;

  doc.setFillColor(...BAND);
  doc.rect(tX, tY, CW, headH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('EARNINGS', tX + 4, tY + 6);
  doc.text('Amount (Rs.)', leftAmtX, tY + 6, { align: 'right' });
  doc.text('DEDUCTIONS', tX + half + 4, tY + 6);
  doc.text('Amount (Rs.)', rightAmtX, tY + 6, { align: 'right' });

  // The leave deduction is its own named deductions line — gross is never
  // silently reduced. The earnings side of that row stays blank.
  const body = [
    [['Basic Salary', d.basic], ['Provident Fund (PF)', d.pf]],
    [['Dearness Allowance (DA)', d.da], ['ESIC / Insurance', d.esic]],
    [['Residual Pay', d.residualPay], ['Professional Tax', d.professionalTax]],
    ...(has(d.lwpDeduction)
      ? [[null, [`Leave Without Pay (${d.daysUnpaid ?? 0} day${(d.daysUnpaid ?? 0) === 1 ? '' : 's'})`, d.lwpDeduction]]]
      : []),
  ];
  let ry = tY + headH;
  doc.setFontSize(9);
  for (const [earn, ded] of body) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK);
    if (earn) {
      doc.text(earn[0], tX + 4, ry + 6);
      doc.text(inr(earn[1]), leftAmtX, ry + 6, { align: 'right' });
    }
    doc.text(ded[0], tX + half + 4, ry + 6);
    doc.text(inr(ded[1]), rightAmtX, ry + 6, { align: 'right' });
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.2);
    doc.line(tX, ry + cellH, tX + CW, ry + cellH);
    ry += cellH;
  }

  // Totals row — bold, heavier separator above. Total Deductions includes the
  // leave deduction, so Gross − Total Deductions = Net Pay on the face of the
  // slip (all stored figures — the arithmetic checks out by construction).
  doc.setDrawColor(...BAND);
  doc.setLineWidth(0.4);
  doc.line(tX, ry, tX + CW, ry);
  doc.setFont('helvetica', 'bold');
  doc.text('Gross Monthly Salary', tX + 4, ry + 6);
  doc.text(inr(d.gross), leftAmtX, ry + 6, { align: 'right' });
  doc.text('Total Deductions', tX + half + 4, ry + 6);
  doc.text(inr(d.totalDeductions), rightAmtX, ry + 6, { align: 'right' });
  ry += cellH;
  const dividerBottom = ry; // the centre divider must not cross the net band

  // NET PAY band — attendance-based slips only (stored net_pay, never
  // recomputed). Full-width, same navy as the header band.
  if (has(d.netPay)) {
    doc.setFillColor(...BAND);
    doc.rect(tX, ry, CW, cellH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('NET PAY', tX + 4, ry + 6.2);
    doc.text(`Rs. ${inr(d.netPay)}`, rightAmtX, ry + 6.2, { align: 'right' });
    doc.setFontSize(9);
    ry += cellH;
  }

  // Outer border + centre divider
  doc.setDrawColor(...BAND);
  doc.setLineWidth(0.5);
  doc.rect(tX, tY, CW, ry - tY);
  doc.line(tX + half, tY, tX + half, dividerBottom);

  // ── Footer notes ────────────────────────────────────────────────────────────
  const fy = ry + 22;
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.4);
  doc.line(M, fy, W - M, fy);
  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.8);
  doc.text('This is a computer-generated salary slip and does not require a signature.', M, fy + 6);
  doc.text('This document is confidential and intended solely for the named employee. For any queries, please contact the HR department.', M, fy + 11);
}

// Build the document for one stored payslip row (joined with its employee).
export function buildPayslipDoc(slip, logo) {
  const emp = slip.employees || {};
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  renderPayslip(doc, {
    logo,
    monthText: monthLabel(slip.month),
    name: emp.name || '—',
    employeeCode: emp.employee_code || '—',
    designation: emp.designation,
    department: emp.department,
    uan: emp.uan,
    esicNumber: emp.esic_number,
    paymentMode: emp.payment_type,
    accountMasked: emp.account_number ? maskAccount(emp.account_number) : null,
    ifsc: emp.ifsc_code,
    basic: slip.basic,
    da: slip.da,
    residualPay: slip.residual_pay,
    gross: slip.gross,
    pf: slip.pf,
    esic: slip.esic,
    professionalTax: slip.professional_tax,
    // Includes the leave deduction so Gross − Total Deductions = Net Pay.
    totalDeductions: (slip.pf ?? 0) + (slip.esic ?? 0) + (slip.professional_tax ?? 0) + (slip.lwp_deduction ?? 0),
    // Attendance figures — NULL on pre-attendance slips (July 2026).
    daysUnpaid: slip.days_unpaid,
    daysNotEmployed: slip.days_not_employed,
    lwpDeduction: slip.lwp_deduction,
    netPay: slip.net_pay,
  });
  return doc;
}
